import type { CLTheme } from 'syf-component-library/defaultTheme'

export const colors = {
  error: '#C63527',
  lightError: '#F9EBE9',
  heavyInfo: '#34657F',
  info: '#0072AD',

  success: '#3E8529',
  lightSuccess: '#f0faee',

  darkWarning: '#CAA416',
  warning: '#FBC600',
  lightWarning: '#F0D675',

  caution: '#F0FAEE',
  icon: '#AA8300',

  lightSeafoam: '#DEEAEB',
  lightBlue: '#E6F2F3',
  lightestBlue: '#EAEFF2',

  white: '#FFFFFF',
  lightestGrey: '#F6F6F6',
  lightGrey: '#E0E0E0',
  heavyGrey: '#9E9E9E',
  mediumGrey: '#656565',
  borderGrey: '#979797',
  steelGrey: '#3B3C43',
  grey: '#cdcdcd',
  black: '#333333',
  orange: '#D76B00',
  cautionBorder: '#A88119',
  cautionBackground: '#FFEFC4',
  mercury: '#FAFAFA'
}

const breaks = {
  huge: '1440px',
  large: '1170px',
  medium: '768px',
  small: '640px',
  xs: '450px'
}

interface PropIndex {
  [key: string]: string
}

const less = {
  lessThanHuge: `(max-width: ${breaks.huge})`,
  lessThanLarge: `(max-width: ${breaks.large})`,
  lessThanMedium: `(max-width: ${breaks.medium})`,
  lessThanSmall: `(max-width: ${breaks.small})`,
  lessThanXS: `(max-width: ${breaks.xs})`
}

const great = {
  greaterThanHuge: `(min-width: ${breaks.huge})`,
  greaterThanLarge: `(min-width: ${breaks.large})`,
  greaterThanMedium: `(min-width: ${breaks.medium})`,
  greaterThanSmall: `(min-width: ${breaks.small})`,
  greaterThanXS: `(min-width: ${breaks.xs})`
}

const createBetween: (start: string, stop: string) => string = (
  start: string,
  stop: string
) =>
  `${(great as PropIndex)[`greaterThan${start}`]} and ${
    (less as PropIndex)[`lessThan${stop}`]
  }`

const between = {
  betweenSmallMedium: createBetween('Small', 'Medium'),
  betweenMediumLarge: createBetween('Medium', 'Large'),
  betweenLargeHuge: createBetween('Large', 'Huge')
}

export const mediaQueries = { ...less, ...great, ...between }
// Colors that will be allowed to be modified by the consumer (themed)
export const theme: CLTheme = {
  link: '#34657F',
  icon: '#34657F',
  // The color used to alter the browser default focus color
  focus: '#34657F',
  // For dropdown hover states, table row hover, and selected item background
  hoverBackground: '#EEF6F7',
  // The colors used to set the default (html) body text color and body background color, not to be confused with the body typography element
  body: {
    background: '#F6F6F6',
    text: '#333333'
  },
  accordion: {
    borderColor: '#e2e2e2',
    headerBackgroundColor: '#ffffff',
    activeHeaderBackgroundColor: '#f5f5f5'
  },
  tooltip: {
    borderColor: '#ffffff',
    backgroundColor: '#ffffff',
    color: '#000000'
  },
  button: {
    primary: {
      text: '#FFFFFF',
      hover: '#2A5166'
    }, // text and icon color
    secondary: {
      hover: '#EBF0F2'
    }, // text and icon color
    text: '#34657F',
    background: '#34657F' // sets bg and border
  },
  binart: {
    background: '#34657f',
    disableBackground: '#ebf0f2',
    disableBorderColor: '#9eb5c2'
  },
  field: {
    icon: '#34657F',
    focus: '#34657F',
    label: '#34657F'
  }, // selects/textfields/textarea
  checkbox: {
    background: colors.success,
    font: '#FFFFFF'
  },
  chip: {
    hover: '#ffe5ad',
    text: '#34657F'
  },
  switch: {
    background: '#34657F'
  },
  radio: {
    border: '#34657F',
    background: '#34657F',
    inactiveBorder: '#656565',
    focusBackground: '#CECFD1'
  },
  navigation: {
    activeTabText: '#34657F',
    activeTabBorder: '#34657F'
  },
  datepicker: {
    navIcon: '#515151',
    shadow: 'rgba(0, 0, 0, 0.12)',
    disabled: '#e9e9e9',
    legend: {
      selectedDate: {
        fill: '#34657f',
        border: '#34657f',
        text: '#ffffff'
      },
      dueDate: {
        fill: 'transparent',
        border: '#34657f',
        text: '#333333'
      },
      lateFeeDate: {
        fill: '#757575',
        border: '#757575',
        text: '#ffffff'
      }
    }
  },
  footer: {
    background: '#3B3C43',
    text: '#FFFFFF',
    link: '#FFFFFF'
  },
  header: {
    background: '#F6F6F6', // headerBackground
    text: '',
    border: '' // just dsecurity: headerDivider
  },
  progressStepBar: {
    fill: '#34657F',
    hover: '#D6E0E5',
    label: '#34657F'
  },
  progressBar: {
    complete: '#3e8529',
    error: '#c63527',
    inProgress: '#0072ad',
    background: '#e9eaeb'
  }
}
